import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Settings, Clock, Target, Edit, Save, X, Palette, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { ColorPicker } from '@/components/ui/color-picker';
import { MultiSelectComboBox } from '@/components/automation/content/shared/MultiSelectComboBox';

interface ScoreLabelBand {
  id: string;
  minScore: number;
  maxScore: number;
  label: string;
  color: string;
}

interface ScoreLabelBands {
  allowHalfPoints: boolean;
  bands: ScoreLabelBand[];
}

interface QASettings {
  delay: { value: number; unit: 'minutes' | 'hours' | 'days' };
  confidenceThreshold: number;
  aiConfidenceThreshold?: number;
  frequency: { type: 'all' | 'percentage' | 'interval'; value: number };
  ratingScale: '1-5' | '1-10' | '0-100';
  acceptableThreshold: number;
  scoreLabelBands: ScoreLabelBands;
  supervisorNotifications: {
    scoreBelow: boolean;
    negativeParameters: boolean;
    selectedParameters: string[];
    method: 'in-app' | 'email';
  };
}

interface ScoringAgentQASettingsProps {
  qaSettings: QASettings;
  onSave: (settings: QASettings) => void;
  agentParameters?: Array<{ name: string; enabled: boolean; }>;
  botParameters?: Array<{ name: string; enabled: boolean; }>;
}

const ScoringAgentQASettings: React.FC<ScoringAgentQASettingsProps> = ({ 
  qaSettings, 
  onSave, 
  agentParameters = [], 
  botParameters = [] 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [formData, setFormData] = useState<QASettings>(qaSettings);

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(qaSettings);
    setIsEditing(false);
  };

  const updateFormData = (path: string, value: any) => {
    setFormData(prev => {
      const keys = path.split('.');
      const result = { ...prev };
      let current: any = result;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return result;
    });
  };

  const addScoreBand = () => {
    const newBand: ScoreLabelBand = {
      id: Date.now().toString(),
      minScore: 0,
      maxScore: 10,
      label: 'New Band',
      color: '#6b7280'
    };
    setFormData(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: [...prev.scoreLabelBands.bands, newBand]
      }
    }));
  };

  const removeBand = (bandId: string) => {
    setFormData(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: prev.scoreLabelBands.bands.filter(band => band.id !== bandId)
      }
    }));
  };

  const updateBand = (bandId: string, field: keyof ScoreLabelBand, value: any) => {
    setFormData(prev => ({
      ...prev,
      scoreLabelBands: {
        ...prev.scoreLabelBands,
        bands: prev.scoreLabelBands.bands.map(band => 
          band.id === bandId ? { ...band, [field]: value } : band
        )
      }
    }));
  };
  
  // Get all available parameters for multi-select
  const getAvailableParameters = () => {
    const agentParams = agentParameters.filter(param => param.enabled).map(param => ({
      id: `agent-${param.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: `Agent: ${param.name}`
    }));
    const botParams = botParameters.filter(param => param.enabled).map(param => ({
      id: `bot-${param.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: `Bot: ${param.name}`
    }));
    return [...agentParams, ...botParams];
  };
  return (
    <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="hover:bg-muted/30 transition-colors">
            <CardTitle className="flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/50 rounded-lg">
                  <Settings className="h-5 w-5 text-secondary-foreground" />
                </div>
                QA Settings
              </div>
              <div className="flex items-center gap-2">
                {!isCollapsed && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                {!isCollapsed && isEditing && (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave}>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {!isEditing ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      QA Delay
                    </label>
                    <p className="text-base">{qaSettings.delay.value} {qaSettings.delay.unit}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      Confidence Threshold
                    </label>
                    <p className="text-base">{qaSettings.confidenceThreshold}%</p>
                  </div>
                  {qaSettings.aiConfidenceThreshold && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">AI Confidence Threshold</label>
                      <p className="text-base">{qaSettings.aiConfidenceThreshold}%</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Rating Scale</label>
                    <p className="text-base">{qaSettings.ratingScale}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Acceptable Threshold</label>
                    <p className="text-base">{qaSettings.acceptableThreshold}{qaSettings.ratingScale === '0-100' ? '%' : ''}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Frequency</label>
                    <p className="text-base capitalize">
                      {qaSettings.frequency.type} 
                      {qaSettings.frequency.type !== 'all' && ` (${qaSettings.frequency.value}${qaSettings.frequency.type === 'percentage' ? '%' : ''})`}
                    </p>
                  </div>
                </div>
                
                <Separator />

                {/* Score Label Bands - Read Only */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Palette className="h-4 w-4" />
                    Score Label Bands
                  </label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        Allow Half Points: {qaSettings.scoreLabelBands?.allowHalfPoints ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {qaSettings.scoreLabelBands?.bands.map((band) => (
                        <div key={band.id} className="flex items-center gap-3 p-2 border rounded-lg">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: band.color }}
                          />
                          <div className="flex-1">
                            <span className="font-medium">{band.label}</span>
                            <span className="text-sm text-muted-foreground ml-2">
                              ({band.minScore} - {band.maxScore})
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Supervisor Notifications</label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={qaSettings.supervisorNotifications.scoreBelow ? 'default' : 'secondary'}>
                        Score Below Threshold: {qaSettings.supervisorNotifications.scoreBelow ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={qaSettings.supervisorNotifications.negativeParameters ? 'default' : 'secondary'}>
                        Negative Parameters: {qaSettings.supervisorNotifications.negativeParameters ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        Method: {qaSettings.supervisorNotifications.method}
                      </Badge>
                    </div>
                    {qaSettings.supervisorNotifications.selectedParameters.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground">Selected Parameters:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {qaSettings.supervisorNotifications.selectedParameters.map((param) => (
                            <Badge key={param} variant="outline" className="text-xs">
                              {param}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>QA Delay Value</Label>
                    <Input
                      type="number"
                      value={formData.delay.value}
                      onChange={(e) => updateFormData('delay.value', parseInt(e.target.value))}
                      min={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>QA Delay Unit</Label>
                    <Select value={formData.delay.unit} onValueChange={(value: 'minutes' | 'hours' | 'days') => updateFormData('delay.unit', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes">Minutes</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Confidence Threshold (%)</Label>
                    <Input
                      type="number"
                      value={formData.confidenceThreshold}
                      onChange={(e) => updateFormData('confidenceThreshold', parseInt(e.target.value))}
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>AI Confidence Threshold (%)</Label>
                    <Input
                      type="number"
                      value={formData.aiConfidenceThreshold || ''}
                      onChange={(e) => updateFormData('aiConfidenceThreshold', e.target.value ? parseInt(e.target.value) : undefined)}
                      min={0}
                      max={100}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rating Scale</Label>
                    <Select value={formData.ratingScale} onValueChange={(value: '1-5' | '1-10' | '0-100') => updateFormData('ratingScale', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-5">1-5</SelectItem>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="0-100">0-100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Acceptable Threshold</Label>
                    <Input
                      type="number"
                      value={formData.acceptableThreshold}
                      onChange={(e) => updateFormData('acceptableThreshold', parseInt(e.target.value))}
                      min={formData.ratingScale === '0-100' ? 0 : 1}
                      max={formData.ratingScale === '1-5' ? 5 : formData.ratingScale === '1-10' ? 10 : 100}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency Type</Label>
                    <Select value={formData.frequency.type} onValueChange={(value: 'all' | 'percentage' | 'interval') => updateFormData('frequency.type', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="interval">Interval</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.frequency.type !== 'all' && (
                    <div className="space-y-2">
                      <Label>Frequency Value</Label>
                      <Input
                        type="number"
                        value={formData.frequency.value}
                        onChange={(e) => updateFormData('frequency.value', parseInt(e.target.value))}
                        min={1}
                        max={formData.frequency.type === 'percentage' ? 100 : undefined}
                      />
                    </div>
                  )}
                </div>

                <Separator />

                {/* Score Label Bands - Editable */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Score Label Bands
                    </Label>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addScoreBand}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Band
                    </Button>
                  </div>

                  {/* Half-point toggle for 1-5 and 1-10 scales */}
                  {(formData.ratingScale === '1-5' || formData.ratingScale === '1-10') && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                      <div>
                        <Label className="text-sm font-medium">Allow Half Points</Label>
                        <p className="text-xs text-muted-foreground">Enable 0.5 increments (e.g., 3.5, 4.5)</p>
                      </div>
                      <Switch
                        checked={formData.scoreLabelBands.allowHalfPoints}
                        onCheckedChange={(checked) => updateFormData('scoreLabelBands.allowHalfPoints', checked)}
                      />
                    </div>
                  )}

                  {/* Score bands list */}
                  <div className="space-y-3">
                    {formData.scoreLabelBands.bands.map((band, index) => (
                      <div key={band.id} className="p-4 border rounded-lg bg-card space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: band.color }}
                            />
                            <span className="font-medium">Band {index + 1}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBand(band.id)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Min Score</Label>
                            <Input
                              type="number"
                              value={band.minScore}
                              onChange={(e) => updateBand(band.id, 'minScore', parseFloat(e.target.value) || 0)}
                              step={formData.scoreLabelBands.allowHalfPoints ? 0.5 : 1}
                              className="text-center"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Max Score</Label>
                            <Input
                              type="number"
                              value={band.maxScore}
                              onChange={(e) => updateBand(band.id, 'maxScore', parseFloat(e.target.value) || 0)}
                              step={formData.scoreLabelBands.allowHalfPoints ? 0.5 : 1}
                              className="text-center"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Label</Label>
                            <Input
                              type="text"
                              value={band.label}
                              onChange={(e) => updateBand(band.id, 'label', e.target.value)}
                              placeholder="Enter label"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Color</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={band.color}
                                onChange={(e) => updateBand(band.id, 'color', e.target.value)}
                                className="w-12 h-8 p-1 cursor-pointer"
                              />
                              <Input
                                type="text"
                                value={band.color}
                                onChange={(e) => updateBand(band.id, 'color', e.target.value)}
                                className="flex-1 text-xs"
                                placeholder="#000000"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label className="text-base font-medium">Supervisor Notifications</Label>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={formData.supervisorNotifications.scoreBelow}
                        onCheckedChange={(checked) => updateFormData('supervisorNotifications.scoreBelow', checked)}
                      />
                      <Label>Notify when score is below threshold</Label>
                    </div>
                    
                     <div className="flex items-center space-x-2">
                       <Switch
                         checked={formData.supervisorNotifications.negativeParameters}
                         onCheckedChange={(checked) => updateFormData('supervisorNotifications.negativeParameters', checked)}
                       />
                       <Label>Notify for negative parameters</Label>
                     </div>
                     
                     {/* Parameter Selection - Show when negative parameters is enabled */}
                     {formData.supervisorNotifications.negativeParameters && (
                       <div className="space-y-2 ml-6 p-3 bg-muted/30 rounded-lg border">
                         <Label className="text-sm font-medium">Specific Parameters to Monitor</Label>
                         <MultiSelectComboBox 
                           items={getAvailableParameters()}
                           selectedItems={formData.supervisorNotifications.selectedParameters || []}
                           onChange={(selectedIds) => updateFormData('supervisorNotifications.selectedParameters', selectedIds)}
                           placeholder="Select parameters to monitor for negative scores"
                           emptyMessage="No parameters available"
                         />
                         {formData.supervisorNotifications.selectedParameters && formData.supervisorNotifications.selectedParameters.length > 0 && (
                           <div className="text-xs text-muted-foreground">
                             {formData.supervisorNotifications.selectedParameters.length} parameter(s) selected for monitoring
                           </div>
                         )}
                       </div>
                     )}
                     
                     <div className="space-y-2">
                       <Label>Notification Method</Label>
                       <Select value={formData.supervisorNotifications.method} onValueChange={(value: 'in-app' | 'email') => updateFormData('supervisorNotifications.method', value)}>
                         <SelectTrigger className="w-48">
                           <SelectValue />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="in-app">In-App</SelectItem>
                           <SelectItem value="email">Email</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ScoringAgentQASettings;